/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License 2.0 which accompany this distribution.
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *   https://www.eclipse.org/legal/epl-2.0/
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import { Box, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AccountDetailView, IntegrationsDelegate } from '@cedalo/webui-extensions';
import { withAppData } from '../../app/AppProvider';
import { runQuery, useQuery } from '../../app/GraphQLWSClient';
import SettingsPaper from '../../utils/SettingsPaper';
import { IntegrationSelectTable } from './IntegrationSelectTable';
import * as Query from './queries';
import { ConnectionIcon } from '../connections/ConnectionIcon';
import { withScopeRights } from '../../utils/with';

const showSelectIntegrationTable = (appId, moduleId, integrations, selectIntegration) => {
	return (
		<IntegrationSelectTable
			integrations={integrations}
			appId={appId}
			type={moduleId}
			onShowIntegrationDetails={selectIntegration}
		/>
	);
};
const showIntegrationAccountDetails = (accountId) => (
	<AccountDetailView accountId={accountId} delegate={IntegrationsDelegate} />
);
const showSelectIntegrationModule = (integrationModules, onSelectIntegrationModule) => {
	return (
		<Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
			{integrationModules.map((im) => (
				<Paper key={im.id} onClick={() => onSelectIntegrationModule(im)} style={{ cursor: 'pointer' }}>
					<Box p={2} sx={{ display: 'flex', flexDirection: 'column' }}>
						<Box sx={{ display: 'grid', gridGap: '8px', gridTemplateColumns: '24px 1fr 24px' }}>
							<ConnectionIcon src={im.logo} />
							<Typography variant="h6">{im.name}</Typography>
						</Box>
						<Typography variant="subtitle1">{im.description}</Typography>
					</Box>
				</Paper>
			))}
		</Box>
	);
};
const showLoadingHint = () => (
	<Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
		<FormattedMessage id="Status.LoadingApp" defaultMessage="Loading..." />
	</Box>
);
const showNotAllowed = () => (
	<Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
		<FormattedMessage
			id="Integrations.NotAllowed"
			defaultMessage="You are not allowed to access Integration Accounts. Contact your adminstrator for more information."
		/>
	</Box>
);

export const IntegrationSelectPanel = withScopeRights(
	withAppData(({ machine }) => ({ appId: machine.id }))(({ appId, onClose, rights }) => {
		const [integrations, setIntegrations] = useState(null);
		const [selectedModule, setSelectedModule] = useState(null);
		const [selectedIntegration, setSelectedIntegration] = useState(null);
		const isAllowed = rights.includes('account.view');

		const onSelectIntegrationModule = async (inmod) => {
			try {
				const result = await runQuery(Query.integrationsByType, { type: inmod.id, machineId: appId });
				setIntegrations(result.integrationsByType);
				setSelectedModule(inmod);
			} catch (error) {
				console.log(error);
				onClose();
			}
		};

		const onBack = () => {
			if (selectedIntegration) setSelectedIntegration(null);
			else setSelectedModule(null);
			// window.location.hash = history;
		};

		const { status = '', result = {} } = isAllowed ? useQuery(Query.integrationModules) : {};
		useEffect(() => {
			if (status === 'error') {
				console.error(result);
			}
		}, [status]);

		const titleId = () => {
			if (selectedIntegration) return { id: 'Extensions.Account.Settings', def: 'Account Settings' };
			return selectedModule
				? { id: 'Integrations.Select.Account.Title', def: 'Select Integration Account' }
				: { id: 'Integrations.Select.Title', def: 'Select Integration' };
		};

		return (
			<SettingsPaper
				open
				close={onClose}
				back={selectedModule ? onBack : undefined}
				helpContext="guides/integrations"
				width="400px"
				title={
					<FormattedMessage
						id={titleId().id}
						defaultMessage={titleId().def}
						values={{ name: selectedModule ? selectedModule.name : '' }}
					/>
				}
			>
				{/* eslint-disable */}
				{status === 'done'
					? selectedIntegration
						? showIntegrationAccountDetails(selectedIntegration)
						: !selectedModule
						? showSelectIntegrationModule(result.integrationModules, onSelectIntegrationModule)
						: integrations
						? showSelectIntegrationTable(appId, selectedModule.id, integrations, setSelectedIntegration)
						: showLoadingHint()
					: isAllowed
					? showLoadingHint()
					: showNotAllowed()}
				{/* eslint-enable */}
			</SettingsPaper>
		);
	})
);
